// Global Instructions Rule Applied!
//
// "What the agent knows" — memory grouped by type with importance dots, the
// pilot-review memgroup format (approved deviation, see
// docs/FRONTEND_DESIGN_PATTERNS.md). Per-memory maintenance actions are
// optional and in-context: source / history / retire. Agent-written content
// only.
import React, { useMemo, useState } from 'react'
import { Modal } from 'antd'
import dayjs from 'dayjs'
import { AgentMemory } from '../../../state_manager/data_models/internal_agents_model.ts'

const IMP_ACCENT = '#3E6FA8'

// Display order + labels for the five memory types (cfg-independent copy —
// the type set is a schema CHECK, not a lookup table).
const MEMORY_TYPE_LABELS: Array<{ key: string; label: string }> = [
  { key: 'profile_fact', label: 'Profile' },
  { key: 'relationship', label: 'People' },
  { key: 'communication_pattern', label: 'Communication patterns' },
  { key: 'kpi_convention', label: 'KPI conventions' },
  { key: 'open_question', label: 'Open questions' }
]

const importanceDots = (importance: number): string =>
  '●'.repeat(Math.max(1, Math.min(5, importance))) + '○'.repeat(5 - Math.max(1, Math.min(5, importance)))

interface AgentMemoryGroupsProps {
  memories: AgentMemory[]
  onOpenInteraction?: (interactionId: string, timeline?: { table?: string | null; id?: string | null } | null) => void
  onRetire?: (memoryId: string) => void
}

export const AgentMemoryGroups: React.FC<AgentMemoryGroupsProps> = ({
  memories,
  onOpenInteraction,
  onRetire
}) => {
  const [historyOf, setHistoryOf] = useState<AgentMemory | null>(null)

  const active = useMemo(() => memories.filter((m) => m.agent_memories__status === 'active'), [memories])
  const byType = useMemo(() => {
    const map = new Map<string, AgentMemory[]>()
    for (const memory of active) {
      const list = map.get(memory.agent_memories__memory_type) ?? []
      list.push(memory)
      map.set(memory.agent_memories__memory_type, list)
    }
    for (const list of map.values()) {
      list.sort((a, b) => b.agent_memories__importance - a.agent_memories__importance)
    }
    return map
  }, [active])

  // Supersede chain for the history drawer: walk backwards via superseded_by_id.
  const historyChain = useMemo(() => {
    if (!historyOf) return []
    const chain: AgentMemory[] = []
    let currentId: string | null = historyOf.agent_memories__id
    const guard = new Set<string>()
    while (currentId && !guard.has(currentId)) {
      guard.add(currentId)
      const predecessor = memories.find((m) => m.agent_memories__superseded_by_id === currentId)
      if (!predecessor) break
      chain.push(predecessor)
      currentId = predecessor.agent_memories__id
    }
    return chain
  }, [historyOf, memories])

  return (
    <div className='flex flex-col gap-2.5'>
      {MEMORY_TYPE_LABELS.filter(({ key }) => (byType.get(key) ?? []).length > 0).map(({ key, label }) => (
        <div key={key} className='bg-white border border-gray-100 rounded-lg px-4 py-2.5'>
          <h5 className='m-0 mb-2 text-[13px] font-semibold'>
            {label} <span className='text-gray-400 font-normal'>{(byType.get(key) ?? []).length}</span>
          </h5>
          <ul className='m-0 p-0 list-none flex flex-col gap-2'>
            {(byType.get(key) ?? []).map((memory) => (
              <li key={memory.agent_memories__id} className='text-[12.5px]'>
                <span
                  className='mr-2 whitespace-nowrap align-[2px]'
                  style={{ color: IMP_ACCENT, fontSize: 8, letterSpacing: 2 }}
                  title={`importance ${memory.agent_memories__importance}/5`}
                >
                  {importanceDots(memory.agent_memories__importance)}
                </span>
                {memory.agent_memories__content}
                <span className='text-gray-400 text-[11.5px] ml-1'>
                  {memory.agent_memories__source_interaction_id && onOpenInteraction ? (
                    <>
                      <button
                        type='button'
                        className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
                        onClick={() =>
                          onOpenInteraction(memory.agent_memories__source_interaction_id as string, {
                            table: memory.agent_memories__source_timeline_table,
                            id: memory.agent_memories__source_timeline_id
                          })
                        }
                      >
                        source
                      </button>
                      {' · '}
                    </>
                  ) : null}
                  {memory.agent_memories__last_confirmed_at
                    ? `confirmed ${dayjs(memory.agent_memories__last_confirmed_at).format('MM/DD/YYYY')} · `
                    : ''}
                  <button
                    type='button'
                    className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
                    onClick={() => setHistoryOf(memory)}
                  >
                    history
                  </button>
                  {onRetire ? (
                    <>
                      {' · '}
                      <button
                        type='button'
                        className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
                        onClick={() => onRetire(memory.agent_memories__id)}
                      >
                        retire
                      </button>
                    </>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}

      <Modal
        title='Memory history'
        open={historyOf != null}
        footer={null}
        onCancel={() => setHistoryOf(null)}
        destroyOnClose
      >
        {historyOf ? (
          <div className='flex flex-col gap-3'>
            <div>
              <div className='text-[11px] uppercase tracking-wide text-gray-400 mb-0.5'>Current</div>
              <div className='text-[12.5px]'>{historyOf.agent_memories__content}</div>
            </div>
            {historyChain.length === 0 ? (
              <div className='text-gray-400 text-xs'>No superseded versions.</div>
            ) : (
              historyChain.map((memory) => (
                <div key={memory.agent_memories__id}>
                  <div className='text-[11px] uppercase tracking-wide text-gray-400 mb-0.5'>
                    Superseded · {dayjs(memory.agent_memories__date_created).format('MM/DD/YYYY')}
                  </div>
                  <div className='text-[12.5px] text-gray-500'>{memory.agent_memories__content}</div>
                </div>
              ))
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  )
}
